import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PlusCircle,
  MinusCircle,
  Clock,
  RefreshCw,
  AlertCircle,
  Info,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  CreditCard,
  TimerOff,
  RotateCcw,
  Inbox,
  Filter,
  Calendar
} from 'lucide-react';
import api from '../../../../api/client';

// ─── Constants ──────────────────────────────────────────────────────────────────
const PAGE_SIZE = 6;

const ENTRY_TYPE_CONFIG = {
  addition: {
    labelKey: 'companyAdmins.creditsTab.typeAllocation',
    fallback: 'Allocation (Addition)',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-200',
    dotColor: 'bg-blue-500',
  },
  usage: {
    labelKey: 'companyAdmins.creditsTab.typeUsage',
    fallback: 'Interview Usage (Deduction)',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-700',
    borderColor: 'border-orange-200',
    dotColor: 'bg-orange-500',
  },
  expiry: {
    labelKey: 'companyAdmins.creditsTab.typeExpiration',
    fallback: 'Expiration',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-600',
    borderColor: 'border-gray-200',
    dotColor: 'bg-gray-400',
  },
  refund: {
    labelKey: 'companyAdmins.creditsTab.typeRefund',
    fallback: 'Refund',
    bgColor: 'bg-teal-50',
    textColor: 'text-teal-700',
    borderColor: 'border-teal-200',
    dotColor: 'bg-teal-500',
  },
  adjustment: {
    labelKey: 'companyAdmins.creditsTab.typeAdjustment',
    fallback: 'Adjustment',
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-700',
    borderColor: 'border-purple-200',
    dotColor: 'bg-purple-500',
  },
};

// ─── Helpers ────────────────────────────────────────────────────────────────────
const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  }) + ' ' + d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  // Check if expiry is 100+ years in future → "Never"
  const now = new Date();
  const diffYears = (d - now) / (1000 * 60 * 60 * 24 * 365);
  if (diffYears > 90) return 'Never';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
};

// ─── Skeleton Components ────────────────────────────────────────────────────────
const SkeletonPulse = ({ className = '' }) => (
  <div className={`animate-pulse rounded bg-gray-200 ${className}`} />
);

const BalanceSkeleton = () => (
  <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
    <div className="flex flex-col lg:flex-row lg:items-center gap-6">
      <div className="flex items-center gap-4 flex-1">
        <SkeletonPulse className="h-16 w-16 rounded-xl" />
        <div className="flex-1">
          <SkeletonPulse className="h-4 w-40 mb-3" />
          <SkeletonPulse className="h-10 w-28 mb-2" />
          <SkeletonPulse className="h-3 w-56" />
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 flex-1">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="rounded-xl border border-gray-100 bg-gray-50 p-4">
            <SkeletonPulse className="h-3 w-6 mx-auto mb-2" />
            <SkeletonPulse className="h-6 w-10 mx-auto mb-1" />
            <SkeletonPulse className="h-3 w-12 mx-auto" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

const LedgerSkeleton = () => (
  <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
    <div className="p-6 border-b border-gray-100">
      <SkeletonPulse className="h-5 w-48 mb-2" />
      <SkeletonPulse className="h-3 w-72" />
    </div>
    <div className="divide-y divide-gray-100">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="flex items-center gap-4 px-6 py-4">
          <SkeletonPulse className="h-4 w-36" />
          <SkeletonPulse className="h-6 w-40 rounded-full" />
          <SkeletonPulse className="h-5 w-12" />
          <SkeletonPulse className="h-4 w-24" />
          <SkeletonPulse className="h-4 w-60 flex-1" />
        </div>
      ))}
    </div>
  </div>
);

// ─── Sub-Components ─────────────────────────────────────────────────────────────

const StatCard = ({ icon: Icon, value, label, color, borderColor }) => (
  <div
    className={`rounded-xl border bg-white p-4 text-center transition-all duration-200 hover:shadow-md ${borderColor}`}
  >
    <div className="flex items-center justify-center mb-1.5">
      <Icon size={16} className={color} />
    </div>
    <p className={`text-xl font-bold ${color}`}>{value ?? '—'}</p>
    <p className="text-xs text-gray-500 font-medium mt-0.5">{label}</p>
  </div>
);

const TypeBadge = ({ entryType }) => {
  const config = ENTRY_TYPE_CONFIG[entryType] || ENTRY_TYPE_CONFIG.adjustment;
  const { t } = useTranslation();
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${config.bgColor} ${config.textColor} ${config.borderColor}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${config.dotColor}`} />
      {t(config.labelKey, config.fallback)}
    </span>
  );
};

const AmountCell = ({ amount }) => {
  const isPositive = amount > 0;
  return (
    <span
      className={`font-bold text-sm tabular-nums ${
        isPositive ? 'text-emerald-600' : 'text-red-500'
      }`}
    >
      {isPositive ? `+${amount}` : amount}
    </span>
  );
};

const EmptyState = ({ t }) => (
  <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
    <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center mb-5 shadow-sm">
      <Inbox size={36} className="text-blue-300" />
    </div>
    <h3 className="text-lg font-semibold text-gray-700 mb-2">
      {t('companyAdmins.creditsTab.emptyTitle', 'No credit history yet')}
    </h3>
    <p className="text-sm text-gray-500 max-w-sm">
      {t(
        'companyAdmins.creditsTab.emptyDescription',
        'Credit transactions will appear here once credits are allocated to this account.'
      )}
    </p>
  </div>
);

const ErrorState = ({ message, onRetry, t }) => (
  <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
    <div className="h-20 w-20 rounded-2xl bg-red-50 flex items-center justify-center mb-5 shadow-sm">
      <AlertCircle size={36} className="text-red-400" />
    </div>
    <h3 className="text-lg font-semibold text-gray-700 mb-2">
      {t('companyAdmins.creditsTab.errorTitle', 'Failed to load credits')}
    </h3>
    <p className="text-sm text-gray-500 max-w-sm mb-5">{message}</p>
    <button
      onClick={onRetry}
      className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-lg shadow-sm transition-all duration-200 hover:opacity-90 hover:scale-[1.02]"
      style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}
    >
      <RefreshCw size={16} />
      {t('companyAdmins.creditsTab.retry', 'Retry')}
    </button>
  </div>
);

// ─── Main Component ─────────────────────────────────────────────────────────────

const CreditsTab = () => {
  const { t } = useTranslation();

  // Data state
  const [lifetimeData, setLifetimeData] = useState(null);
  const [balanceData, setBalanceData] = useState(null);

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [showTooltip, setShowTooltip] = useState(false);
  const [filterType, setFilterType] = useState('all');

  // ── Data Fetching ───────────────────────────────────────────────────────────
  const fetchCreditsData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [lifetimeRes, balanceRes] = await Promise.all([
        api.get('credits/lifetime/'),
        api.get('credits/balance/'),
      ]);
      setLifetimeData(lifetimeRes.data);
      setBalanceData(balanceRes.data);
    } catch (err) {
      console.error('Error fetching credits data:', err);
      setError(
        err.response?.data?.error ||
          err.response?.data?.detail ||
          t('companyAdmins.creditsTab.fetchError', 'Failed to retrieve credit data. Please try again.')
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchCreditsData();
  }, [fetchCreditsData]);

  // ── Build a map from bucket_id → bucket for expiry lookups ────────────────
  const bucketMap = useMemo(() => {
    if (!lifetimeData?.buckets) return {};
    const map = {};
    lifetimeData.buckets.forEach((b) => {
      map[b.id] = b;
    });
    return map;
  }, [lifetimeData]);

  // ── Derived: compute refunded total from ledger entries ────────────────────
  const lifetimeRefunded = useMemo(() => {
    if (!lifetimeData?.ledger) return 0;
    return lifetimeData.ledger
      .filter((e) => e.entry_type === 'refund')
      .reduce((sum, e) => sum + Math.abs(e.amount), 0);
  }, [lifetimeData]);

  // ── Consolidated Ledger ───────────────────────────────────────────────────
  const consolidatedLedger = useMemo(() => {
    if (!lifetimeData?.ledger) return [];
    
    const consolidated = [];
    const interviewMap = new Map();
    
    lifetimeData.ledger.forEach(entry => {
      if (entry.entry_type === 'usage' && entry.interview_id) {
        if (interviewMap.has(entry.interview_id)) {
          const existing = interviewMap.get(entry.interview_id);
          existing.amount += entry.amount;
          existing.notes = `Total usage for interview #${entry.interview_id}`;
          
          if (new Date(entry.created_at) > new Date(existing.created_at)) {
            existing.created_at = entry.created_at;
          }
          if (existing.bucket_id !== entry.bucket_id) {
            existing.multiple_buckets = true;
          }
        } else {
          interviewMap.set(entry.interview_id, { ...entry });
        }
      } else {
        consolidated.push({ ...entry });
      }
    });
    
    return [...consolidated, ...Array.from(interviewMap.values())];
  }, [lifetimeData]);

  // ── Filtered + sorted ledger ──────────────────────────────────────────────
  const filteredLedger = useMemo(() => {
    if (!consolidatedLedger) return [];
    let entries = [...consolidatedLedger];

    if (filterType !== 'all') {
      entries = entries.filter((e) => e.entry_type === filterType);
    }

    entries.sort((a, b) => {
      let aVal, bVal;
      if (sortConfig.key === 'created_at') {
        aVal = new Date(a.created_at).getTime();
        bVal = new Date(b.created_at).getTime();
      } else if (sortConfig.key === 'amount') {
        aVal = a.amount;
        bVal = b.amount;
      } else if (sortConfig.key === 'entry_type') {
        aVal = a.entry_type;
        bVal = b.entry_type;
      } else {
        aVal = a[sortConfig.key];
        bVal = b[sortConfig.key];
      }
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return entries;
  }, [lifetimeData, filterType, sortConfig]);

  // ── Pagination ────────────────────────────────────────────────────────────
  const totalPages = Math.ceil(filteredLedger.length / PAGE_SIZE);
  const paginatedLedger = filteredLedger.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  // Reset page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filterType]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
    setCurrentPage(1);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown size={14} className="text-gray-400" />;
    return sortConfig.direction === 'asc' ? (
      <ArrowUp size={14} className="text-blue-600" />
    ) : (
      <ArrowDown size={14} className="text-blue-600" />
    );
  };

  // Build page number array with ellipsis
  const getPageNumbers = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [];
    if (currentPage <= 3) {
      pages.push(1, 2, 3, '...', totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(1, '...', totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
    }
    return pages;
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <BalanceSkeleton />
        <LedgerSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorState message={error} onRetry={fetchCreditsData} t={t} />
      </div>
    );
  }

  const balance = balanceData?.current_balance ?? 0;
  const totalAdded = lifetimeData?.lifetime_total_added ?? 0;
  const totalUsed = lifetimeData?.lifetime_total_used ?? 0;
  const totalExpired = lifetimeData?.lifetime_total_expired ?? 0;

  return (
    <div className="p-6 space-y-6">
      {/* ── Balance Overview Widget ────────────────────────────────────────── */}
      <div className="rounded-2xl border border-gray-200/90 bg-white shadow-sm overflow-hidden">
        <div className="flex flex-col lg:flex-row lg:items-center gap-6 p-6">
          {/* Main Balance */}
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div
              className="h-16 w-16 flex-shrink-0 flex items-center justify-center rounded-xl shadow-sm"
              style={{ background: 'linear-gradient(135deg, #e0e7ff 0%, #ede9fe 100%)' }}
            >
              <CreditCard size={28} className="text-indigo-600" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-sm font-semibold text-gray-600 tracking-wide">
                  {t('companyAdmins.creditsTab.currentBalance', 'Current Available Credits')}
                </h3>
                {/* Tooltip */}
                <div className="relative">
                  <button
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                    onClick={() => setShowTooltip(!showTooltip)}
                    className={`transition-all duration-300 ${
                      showTooltip ? 'text-indigo-600 scale-110' : 'text-gray-400 hover:text-indigo-500 hover:scale-110'
                    }`}
                    aria-label={t('companyAdmins.creditsTab.balanceInfoLabel', 'Balance information')}
                  >
                    <Info size={16} />
                  </button>
                  <div
                    className={`absolute z-50 left-1/2 -translate-x-1/2 top-full mt-3 w-72 p-4 bg-white text-xs rounded-xl shadow-[0_12px_40px_rgba(79,70,229,0.2)] border border-gray-100 border-t-[3px] border-t-indigo-500 transition-all duration-300 origin-top ${
                      showTooltip
                        ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto'
                        : 'opacity-0 -translate-y-2 scale-95 pointer-events-none'
                    }`}
                  >
                    <p className="mb-2 font-bold text-gray-900">
                      {t('companyAdmins.creditsTab.tooltipTitle', 'How is this calculated?')}
                    </p>
                    <p className="text-gray-600 leading-relaxed">
                      {balanceData?.formula ||
                        t(
                          'companyAdmins.creditsTab.tooltipDescription',
                          'This balance reflects all active, non-expired credits available for use.'
                        )}
                    </p>
                    {balanceData?.contributing_buckets_count != null && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <p className="text-indigo-600 font-medium text-[11px]">
                          {balanceData.contributing_buckets_count} of {balanceData.total_buckets_count}{' '}
                          {t('companyAdmins.creditsTab.bucketsActive', 'buckets active')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <p
                className="text-4xl font-extrabold tracking-tight"
                style={{ color: '#4f46e5' }}
              >
                {balance.toLocaleString()}{' '}
                <span className="text-base font-semibold text-gray-500">
                  {t('companyAdmins.creditsTab.credits', 'Credits')}
                </span>
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {t(
                  'companyAdmins.creditsTab.balanceFootnote',
                  'This balance reflects all active, non-expired credits.'
                )}
              </p>
            </div>
          </div>

          {/* Stat Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 flex-1">
            <StatCard
              icon={PlusCircle}
              value={totalAdded}
              label={t('companyAdmins.creditsTab.totalAdded', 'Total Added')}
              color="text-emerald-600"
              borderColor="border-emerald-100"
            />
            <StatCard
              icon={MinusCircle}
              value={totalUsed}
              label={t('companyAdmins.creditsTab.totalUsed', 'Total Used')}
              color="text-red-500"
              borderColor="border-red-100"
            />
            <StatCard
              icon={TimerOff}
              value={totalExpired}
              label={t('companyAdmins.creditsTab.expired', 'Expired')}
              color="text-amber-500"
              borderColor="border-amber-100"
            />
            <StatCard
              icon={RotateCcw}
              value={lifetimeRefunded}
              label={t('companyAdmins.creditsTab.refunded', 'Refunded')}
              color="text-blue-500"
              borderColor="border-blue-100"
            />
          </div>
        </div>

        {/* Next expiry warning */}
        {balanceData?.next_expiry && (
          <div className="px-6 pb-4">
            <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm">
              <Clock size={16} className="text-amber-500 flex-shrink-0" />
              <span className="text-amber-800">
                <span className="font-semibold">{balanceData.next_expiry_amount}</span>{' '}
                {t('companyAdmins.creditsTab.expiringOn', 'credits expiring on')}{' '}
                <span className="font-semibold">{formatDate(balanceData.next_expiry)}</span>
              </span>
            </div>
          </div>
        )}
      </div>

      {/* ── Credit Ledger History ──────────────────────────────────────────── */}
      <div className="rounded-2xl border border-gray-200/90 bg-white shadow-sm overflow-hidden">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-6 py-5 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {t('companyAdmins.creditsTab.ledgerTitle', 'Credit Ledger History')}
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {t(
                'companyAdmins.creditsTab.ledgerDescription',
                'A record of all credit transactions in your account.'
              )}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Filter dropdown */}
            <div className="relative">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="appearance-none pl-8 pr-4 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors cursor-pointer"
                aria-label={t('companyAdmins.creditsTab.filterLabel', 'Filter by type')}
              >
                <option value="all">{t('companyAdmins.creditsTab.filterAll', 'All Types')}</option>
                <option value="addition">{t('companyAdmins.creditsTab.typeAllocation', 'Allocation (Addition)')}</option>
                <option value="usage">{t('companyAdmins.creditsTab.typeUsage', 'Interview Usage (Deduction)')}</option>
                <option value="expiry">{t('companyAdmins.creditsTab.typeExpiration', 'Expiration')}</option>
                <option value="refund">{t('companyAdmins.creditsTab.typeRefund', 'Refund')}</option>
                <option value="adjustment">{t('companyAdmins.creditsTab.typeAdjustment', 'Adjustment')}</option>
              </select>
              <Filter size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Ledger Content */}
        {filteredLedger.length === 0 ? (
          <EmptyState t={t} />
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm" role="table" aria-label="Credit ledger history">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-100">
                    <th
                      className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700 transition-colors"
                      onClick={() => handleSort('created_at')}
                      aria-sort={sortConfig.key === 'created_at' ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        {t('companyAdmins.creditsTab.colDateTime', 'Date & Time')}
                        {getSortIcon('created_at')}
                      </span>
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {t('companyAdmins.creditsTab.colType', 'Transaction Type')}
                    </th>
                    <th
                      className="text-center px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700 transition-colors"
                      onClick={() => handleSort('amount')}
                      aria-sort={sortConfig.key === 'amount' ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                    >
                      <span className="inline-flex items-center gap-1.5 justify-center">
                        {t('companyAdmins.creditsTab.colAmount', 'Amount')}
                        {getSortIcon('amount')}
                      </span>
                    </th>
                    <th className="text-center px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {t('companyAdmins.creditsTab.colInterviewId', 'Interview ID')}
                    </th>
                    <th className="text-center px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {t('companyAdmins.creditsTab.colBucketId', 'Bucket ID')}
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {t('companyAdmins.creditsTab.colExpiry', 'Expiration Date')}
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {t('companyAdmins.creditsTab.colNotes', 'Details / Notes')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedLedger.map((entry) => {
                    const bucket = entry.bucket_id ? bucketMap[entry.bucket_id] : null;
                    return (
                      <tr
                        key={entry.id}
                        className="hover:bg-gray-50/60 transition-colors duration-150"
                      >
                        <td className="px-6 py-4 text-gray-700 whitespace-nowrap">
                          {formatDateTime(entry.created_at)}
                        </td>
                        <td className="px-6 py-4">
                          <TypeBadge entryType={entry.entry_type} />
                        </td>
                        <td className="px-6 py-4 text-center">
                          <AmountCell amount={entry.amount} />
                        </td>
                        <td className="px-6 py-4 text-center text-gray-500 font-mono text-xs">
                          {entry.interview_id ? `#${entry.interview_id}` : '—'}
                        </td>
                        <td className="px-6 py-4 text-center text-gray-500 font-mono text-xs">
                          {entry.multiple_buckets ? 'Multiple' : (entry.bucket_id ? `#${entry.bucket_id}` : '—')}
                        </td>
                        <td className="px-6 py-4 text-gray-600 whitespace-nowrap">
                          {bucket ? formatDate(bucket.expiry_date) : '—'}
                        </td>
                        <td className="px-6 py-4 text-gray-600 max-w-xs truncate" title={entry.notes}>
                          {entry.notes || '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Card Layout */}
            <div className="md:hidden divide-y divide-gray-100">
              {paginatedLedger.map((entry) => {
                const bucket = entry.bucket_id ? bucketMap[entry.bucket_id] : null;
                return (
                  <div key={entry.id} className="p-4 hover:bg-gray-50/60 transition-colors">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <TypeBadge entryType={entry.entry_type} />
                      <AmountCell amount={entry.amount} />
                    </div>
                    <div className="space-y-1.5 text-sm">
                      <div className="flex items-center gap-2 text-gray-500">
                        <Calendar size={13} className="flex-shrink-0" />
                        <span>{formatDateTime(entry.created_at)}</span>
                        {entry.interview_id && (
                          <span className="ml-auto text-xs font-mono text-gray-400">Interview #{entry.interview_id}</span>
                        )}
                        {(entry.bucket_id || entry.multiple_buckets) && (
                          <span className="ml-2 text-xs font-mono text-gray-400">
                            Bucket {entry.multiple_buckets ? 'Multiple' : `#${entry.bucket_id}`}
                          </span>
                        )}
                      </div>
                      {bucket && (
                        <div className="flex items-center gap-2 text-gray-500">
                          <Clock size={13} className="flex-shrink-0" />
                          <span>
                            {t('companyAdmins.creditsTab.expiresLabel', 'Expires:')}{' '}
                            {formatDate(bucket.expiry_date)}
                          </span>
                        </div>
                      )}
                      {entry.notes && (
                        <p className="text-gray-600 text-xs leading-relaxed pt-1">
                          {entry.notes}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
                <p className="text-sm text-gray-500">
                  {t('companyAdmins.creditsTab.showing', 'Showing')}{' '}
                  <span className="font-medium text-gray-700">
                    {(currentPage - 1) * PAGE_SIZE + 1}
                  </span>{' '}
                  {t('companyAdmins.creditsTab.to', 'to')}{' '}
                  <span className="font-medium text-gray-700">
                    {Math.min(currentPage * PAGE_SIZE, filteredLedger.length)}
                  </span>{' '}
                  {t('companyAdmins.creditsTab.of', 'of')}{' '}
                  <span className="font-medium text-gray-700">{filteredLedger.length}</span>{' '}
                  {t('companyAdmins.creditsTab.entries', 'entries')}
                </p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    aria-label="Previous page"
                  >
                    <ChevronLeft size={16} />
                    {t('companyAdmins.creditsTab.previous', 'Previous')}
                  </button>

                  {getPageNumbers().map((page, idx) =>
                    page === '...' ? (
                      <span key={`ellipsis-${idx}`} className="px-2 py-1.5 text-sm text-gray-400">
                        …
                      </span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`min-w-[36px] px-2 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                          currentPage === page
                            ? 'text-white shadow-sm'
                            : 'text-gray-600 bg-white border border-gray-200 hover:bg-gray-50'
                        }`}
                        style={
                          currentPage === page
                            ? { background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }
                            : undefined
                        }
                        aria-label={`Page ${page}`}
                        aria-current={currentPage === page ? 'page' : undefined}
                      >
                        {page}
                      </button>
                    )
                  )}

                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    aria-label="Next page"
                  >
                    {t('companyAdmins.creditsTab.next', 'Next')}
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CreditsTab;
